import type {
  GroundTruthLabel,
  LabelSource,
  ModeChange,
  PointerInputEvent,
  Recording,
  RecordingMeta,
  SignalFrame,
} from "./types.ts";

/**
 * Captures a session as signals plus ground-truth labels.
 *
 * What is stored is the whole eye region and all 52 blendshapes, not just the
 * EAR the current algorithm happens to use. That costs ~2.5 KB/s and buys the
 * ability to change the EAR formula, the landmark indices or the detection route
 * later and still re-evaluate against recordings captured today. Fixtures that
 * only survive until the next algorithm change are barely worth capturing.
 *
 * And it stores no pixels — which is what makes committing a fixture to the repo
 * a non-decision rather than a privacy question.
 */
/** Shortest believable gap between two distinct blinks. */
export const MIN_LABEL_GAP_MS = 150;

/**
 * Minimum gap between recorded drag samples — 20Hz.
 *
 * The mechanic still receives every move at full rate; only the recording is
 * thinned. Steering is a smooth low-frequency gesture and reconstructs from
 * 20Hz without visible loss, at a third of the bytes.
 */
const MIN_MOVE_GAP_MS = 50;

export class Recorder {
  #frames: SignalFrame[] = [];
  #labels: GroundTruthLabel[] = [];
  #inputs: PointerInputEvent[] = [];
  #modeChanges: ModeChange[] = [];
  #lastMoveAt = Number.NEGATIVE_INFINITY;
  #recording = false;
  #lastFrameT = 0;

  get isRecording(): boolean {
    return this.#recording;
  }

  get frameCount(): number {
    return this.#frames.length;
  }

  get labelCount(): number {
    return this.#labels.length;
  }

  /** Seconds of signal captured so far. */
  get durationMs(): number {
    const first = this.#frames[0];
    return first ? this.#lastFrameT - first.t : 0;
  }

  start(): void {
    this.#frames = [];
    this.#labels = [];
    this.#inputs = [];
    this.#modeChanges = [];
    this.#lastMoveAt = Number.NEGATIVE_INFINITY;
    this.#recording = true;
  }

  /**
   * Captures thumb input so a play session replays completely, not half:
   * a press, a release, or a decimated sample of a drag.
   *
   * Drags are thinned and presses never are, because they are not the same
   * kind of fact. A press is an instant that has to survive exactly; a drag is
   * a path, and a path reconstructs fine from a quarter of its samples.
   *
   * The reason it matters: a real 129-second session already serialises to
   * 3.2 MB against the upload endpoint's 4 MB ceiling, so an undecimated 60Hz
   * drag channel is enough on its own to start returning 413 on the longer
   * sessions — which are the ones worth having.
   */
  input(event: PointerInputEvent): void {
    if (!this.#recording) return;
    if (event.phase === "move") {
      if (event.t - this.#lastMoveAt < MIN_MOVE_GAP_MS) return;
      this.#lastMoveAt = event.t;
    }
    this.#inputs.push(event);
  }

  /**
   * Notes what a tap currently means. Idempotent — repeats are dropped.
   *
   * Call it whenever mode or mechanic settles, including at the start of a
   * recording, so the track always opens with the state the session began in
   * and every later entry is a genuine change.
   */
  noteMode(t: number, mode: string, mechanic: string): void {
    if (!this.#recording) return;
    const last = this.#modeChanges[this.#modeChanges.length - 1];
    if (last && last.mode === mode && last.mechanic === mechanic) return;
    this.#modeChanges.push({ t, mode, mechanic });
  }

  /** Milliseconds spent with taps meaning "I blinked" rather than a game verb. */
  labellingMs(endT: number): number {
    let total = 0;
    for (let i = 0; i < this.#modeChanges.length; i++) {
      const change = this.#modeChanges[i] as ModeChange;
      if (change.mode !== "debug") continue;
      total += (this.#modeChanges[i + 1]?.t ?? endT) - change.t;
    }
    return total;
  }

  stop(): void {
    this.#recording = false;
  }

  push(frame: SignalFrame): void {
    if (!this.#recording) return;
    this.#frames.push(frame);
    this.#lastFrameT = frame.t;
  }

  /**
   * Marks a blink the human says happened.
   *
   * @param t when the label is anchored, in the same frame-time base as the
   *   signal. For metronome labels this is the beat; for keytaps it is the
   *   moment of the press, which trails the actual blink by the subject's
   *   reaction time — see estimateLabelOffset in eval/match.ts, which measures
   *   that lag rather than pretending it is zero.
   */
  label(t: number, source: LabelSource): boolean {
    if (!this.#recording) return false;

    // A blink cannot follow another within ~150ms — the closure alone lasts
    // 100–400ms. Taps that close together are one intent registered twice
    // (finger bounce, or hedging), and letting them through inflates the label
    // count against which recall is measured, making the detector look far
    // worse than it is. The first real labelled session had 34 of 76 taps under
    // 300ms apart, with a minimum of 125ms.
    const last = this.#labels[this.#labels.length - 1];
    if (last && t - last.t < MIN_LABEL_GAP_MS) return false;

    this.#labels.push({ t, kind: "blink", source });
    return true;
  }

  /**
   * Frames divided by elapsed time — the mean rate, not the median interval.
   *
   * The median was wrong in a way that flattered the result. Throttling to 24fps
   * from a 30fps camera produces gaps alternating 34ms/67ms; the median gap is
   * 34ms, which reads as 29.4fps, while the true rate is 24.0. Every one of the
   * first 24fps sessions reported 29.4 for exactly this reason. A rate metric
   * has to count frames over time or it is measuring the wrong thing.
   */
  get measuredFps(): number {
    const first = this.#frames[0];
    const last = this.#frames[this.#frames.length - 1];
    if (!first || !last || this.#frames.length < 3) return Number.NaN;
    const span = last.t - first.t;
    return span > 0 ? ((this.#frames.length - 1) * 1000) / span : Number.NaN;
  }

  build(meta: Omit<RecordingMeta, "recordedAt" | "userAgent">, userAgent: string): Recording {
    const measured = this.measuredFps;
    return {
      version: 1,
      meta: {
        ...meta,
        fps: Number.isFinite(measured) ? Number(measured.toFixed(1)) : meta.fps,
        targetFps: meta.fps,
        recordedAt: new Date().toISOString(),
        userAgent,
      },
      frames: this.#frames,
      labels: [...this.#labels].sort((a, b) => a.t - b.t),
      inputs: this.#inputs,
      modeChanges: this.#modeChanges,
    };
  }
}

/**
 * Rounds the floats before serialising. Full double precision on landmark pixel
 * coordinates is noise, and it roughly triples the file size of every fixture.
 */
export function serializeRecording(recording: Recording): string {
  // Non-finite values pass through untouched; JSON.stringify renders them as
  // null, and parseRecording turns null back into NaN on the way in.
  const round = (v: number, dp: number) => (Number.isFinite(v) ? Number(v.toFixed(dp)) : v);

  const frames: SignalFrame[] = recording.frames.map((f) => ({
    ...f,
    ear: { left: round(f.ear.left, 4), right: round(f.ear.right, 4) },
    blink: { left: round(f.blink.left, 4), right: round(f.blink.right, 4) },
    squint: { left: round(f.squint.left, 4), right: round(f.squint.right, 4) },
    pose: {
      pitch: round(f.pose.pitch, 4),
      yaw: round(f.pose.yaw, 4),
      roll: round(f.pose.roll, 4),
    },
    ipd: round(f.ipd, 2),
    t: round(f.t, 1),
    eyeLandmarks: f.eyeLandmarks.map((v) => round(v, 2)),
    blendshapes: f.blendshapes.map((v) => round(v, 4)),
  }));

  // Inputs were serialised raw, which for a drag channel is most of its cost:
  // an unrounded normalised coordinate is twenty characters of noise below the
  // resolution of any screen it will ever be replayed on.
  const inputs = recording.inputs?.map((i) => ({
    ...i,
    t: round(i.t, 1),
    x: round(i.x, 3),
    y: round(i.y, 3),
  }));

  return JSON.stringify(
    inputs ? { ...recording, frames, inputs } : { ...recording, frames },
    null,
    0,
  );
}
